/**
 * HRO (High Reliability Organization) Constants
 *
 * Codes (PF/RS/SO/CR/DE) are canonical: they are what the API stores and what
 * every comparison should use. Labels are display text and are translated.
 *
 * `useReportTags` round-trips through labels — it converts stored codes to
 * labels for the chip UI, then converts the user's edited chips back to codes
 * before PATCHing. That means `hroLabelsToCodes` has to recognise a label in
 * *any* supported language, not just the active one: a user can switch language
 * with chips already on screen. It also still recognises the original English
 * labels, so report data written before this change keeps resolving.
 */

import i18n, { SUPPORTED_LANGUAGES, t } from '../../i18n';

/** The canonical HRO codes, in display order. */
export const HRO_CODES = ['PF', 'RS', 'SO', 'CR', 'DE'];

/**
 * The English labels this module shipped with, before they moved into catalogs.
 * Retained purely so previously stored labels still map back to their code.
 */
const LEGACY_ENGLISH_LABELS = {
  'Preoccupation with Failure': 'PF',
  'Reluctance to Simplify': 'RS',
  'Sensitivity to Operations': 'SO',
  'Commitment to Resilience': 'CR',
  'Deference to Expertise': 'DE',
};

/**
 * Translated label for one HRO code.
 * @param {string} code - 'PF' | 'RS' | 'SO' | 'CR' | 'DE'
 * @returns {string}
 */
export function hroLabel(code) {
  return t(`reports:hro.full.${code}`);
}

/**
 * All HRO labels in the active language, in display order.
 * @returns {string[]}
 */
export function hroLabels() {
  return HRO_CODES.map(hroLabel);
}

/** Match forgivingly — casing and stray whitespace should not break a lookup. */
const normalize = (label) => String(label).trim().toLowerCase();

let reverseLookup = null;

/**
 * label -> code across every supported language, plus the legacy English labels.
 *
 * Built once and cached: catalogs are bundled, so the map is complete and
 * language-independent the moment i18n is initialized.
 */
function getReverseLookup() {
  if (reverseLookup) return reverseLookup;

  const map = {};
  for (const language of SUPPORTED_LANGUAGES) {
    const translate = i18n.getFixedT(language, 'reports');
    for (const code of HRO_CODES) {
      map[normalize(translate(`hro.full.${code}`))] = code;
      map[normalize(translate(`hro.short.${code}`))] = code;
    }
  }
  for (const [label, code] of Object.entries(LEGACY_ENGLISH_LABELS)) {
    map[normalize(label)] = code;
  }

  reverseLookup = map;
  return map;
}

/**
 * Convert HRO codes to labels in the active language
 * @param {string[]} codes - Array of HRO codes
 * @returns {string[]} Array of HRO labels
 */
export function hroCodesToLabels(codes) {
  if (!Array.isArray(codes)) return [];
  return codes.filter((code) => HRO_CODES.includes(code)).map(hroLabel);
}

/**
 * Convert HRO labels back to codes, accepting any supported language
 * @param {string[]} labels - Array of HRO labels
 * @returns {string[]} Array of HRO codes
 */
export function hroLabelsToCodes(labels) {
  if (!Array.isArray(labels)) return [];
  const lookup = getReverseLookup();
  return labels.map((label) => lookup[normalize(label)]).filter(Boolean);
}
